import {
  backgroundColor,
  baseline,
  bottom,
  color,
  marginBottom,
  marginRight,
  marginTop,
  padding,
  paddingH,
  paddingV,
  paddingTop,
  themeColor,
  themeFontTitleWeight,
  borderRadius,
  stylesIfPropTrue,
  mq,
} from '@sleepstation/style-api-helpers'
import {
  Icon,
  ResponsiveImage,
  SubsectionTitle,
} from '@sleepstation/components'
import { LinkBase } from '@sleepstation/components-base'
import { CopyParagraph } from '@sleepstation/components-copy'
import { HLayout, VLayout } from '@sleepstation/components-layouts'
import { LAYOUT_ALIGN, LAYOUT_SPACING, SIZE } from '@sleepstation/const'
import { outset } from '@sleepstation/style-mixins'
import PropTypes from 'prop-types'
import React from 'react'
import styled, { css } from 'styled-components'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const IMAGE_ASPECT_RATIO = 134 / 83

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Link = styled(LinkBase).attrs(({ id }) => ({
  'data-cy': `${id}-card`,
}))`
  text-decoration-line: none;
  display: block;
  cursor: pointer;
  height: 100%;
`

const CallToActionText = styled(HLayout).attrs({
  spacing: 0.05,
  align: LAYOUT_ALIGN.CENTER,
})`
  ${color(`body`)};

  > *:last-child {
    position: relative;
    top: 2px;
  }
`

const Label = styled(CopyParagraph)`
  ${color(`body`)};
  ${baseline(2)}
  font-weight: ${themeFontTitleWeight(`demi`)};
`

const Layout = styled(VLayout).attrs({
  spacing: [LAYOUT_SPACING.BETWEEN],
})`
  position: relative;
  min-height: 100%;
  ${padding([1, 1, 1.25])}
  ${outset(SIZE.LARGE, `background`)}
  background: ${themeColor(`background`)};
  background-position:
    top 5px right 22px,
    top left;
  background-repeat: no-repeat;
  background-size: 161px;
  overflow: hidden;
`

const Primary = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.MEDIUM,
})`
  > * {
    max-width: 380px;
    ${mq(`smallOnly`)} {
      max-width: 82%;
    }
    ${mq(`mediumUp`)} {
      max-width: 95%;
    }
  }

  ${CopyParagraph} {
    ${baseline(1)}
    ${mq(`mediumUp`)} {
      ${baseline(0.5)}
    }
  }
`

const PrimaryTitle = styled(SubsectionTitle)`
  ${stylesIfPropTrue(
    `limitWidth`,
    css`
      max-width: 82%;
    `
  )}
`

const Secondary = styled.div`
  position: relative;
  ${marginBottom([-1, -1, 0])}
  ${bottom([0, 0, -1.25])}
  ${paddingTop(0.3)}
  align-self: flex-end;
  ${marginTop([0, -1, -1.75, -2])}
  ${marginRight([0])}

  > * {
    width: 134px;
  }
`

const Badge = styled.div.attrs({
  'data-cy': `link-card-badge`,
})`
  height: 85px;
  width: 85px;
  position: absolute;
  top: -20px;
  right: -5px;
  ${paddingH(0.5)}
  ${paddingV(1)}
  ${borderRadius(2)}
  ${color(`outset`)}
  ${({ backgroundColour }) => css`
    ${backgroundColor(backgroundColour)}
  `}
  ${baseline(1)}
  font-weight: bold;
  ${mq(`mediumUp`)} {
    height: 90px;
    width: 90px;
  }
`

const BadgeCopy = styled.div`
  margin-top: 7px;
  text-align: center;
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const LinkCard = ({ id, title, content, subContent, link, image, badge }) => {
  const { maximumScore, score, backgroundColour, showBadge } = badge

  return (
    <Link id={id} href={link.url} forceOpenInSameWindow>
      <Layout>
        {showBadge && (
          <Badge backgroundColour={backgroundColour}>
            <BadgeCopy>
              {score} / {maximumScore}
            </BadgeCopy>
          </Badge>
        )}
        <Primary>
          <PrimaryTitle shouldPreventWidows={false} limitWidth={showBadge}>
            {title}
          </PrimaryTitle>
          <CopyParagraph>{content}</CopyParagraph>
          {subContent && <CopyParagraph>{subContent}</CopyParagraph>}
          <CallToActionText>
            <Label>{link.label}</Label>
            {` `}
            <Icon name="next" size={SIZE.MEDIUM} />
          </CallToActionText>
        </Primary>
        <Secondary>
          <ResponsiveImage
            aspectRatio={IMAGE_ASPECT_RATIO}
            src={image.url}
            alt={image.alt}
          />
        </Secondary>
      </Layout>
    </Link>
  )
}

LinkCard.propTypes = {
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  subContent: PropTypes.string,
  link: PropTypes.object.isRequired,
  image: PropTypes.object.isRequired,
  badge: PropTypes.object,
}

LinkCard.defaultProps = {
  subContent: ``,
  badge: {},
}

export default LinkCard
